import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AUTH_URL } from '../../features/apis/baseFectch';
import { FiKey } from 'react-icons/fi';
import { PiMagicWand } from 'react-icons/pi';
import ReactToastify from '../../customHook/useReactToastify';

interface AccessModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function AccessModal({ open, setOpen }: AccessModalProps) {
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (isLoading) return;

    setError('');

    const cleanedCode = code.trim().toUpperCase();

    if (!cleanedCode) {
      setError('Access code is required');
      return;
    }

    try {
      setIsLoading(true);
      const res = await axios.post(`${AUTH_URL}/access-codes/validate`, {
        code: cleanedCode,
      });
      const data = res.data;
      localStorage.setItem('access_code', data.data.code);

      ReactToastify({
        success: true,
        message: 'Access code validated successfully',
      });

      setOpen(false);

      const authToken = localStorage.getItem('token');

      if (authToken) {
        navigate('/');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Invalid or expired code';

      setError(message);

      ReactToastify({
        success: false,
        message: 'Access code validation failed',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xl"
    >
      <div className="relative w-full max-w-sm mx-4 bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden">
        <div className="h-0.75 w-full bg-linear-to-r from-[#6E3EF5] to-[#A818D8]" />

        <div className="p-8">
          <div className="flex items-center gap-2.5 mb-5 justify-center">
            <img
              src="/tara.svg"
              alt="App logo"
              width={40}
              height={40}
              className="object-contain"
            />
          </div>

          <div className="flex items-center mb-2 gap-2 justify-center">
            <FiKey className="w-5 h-5 text-[#6E3EF5]" />
            <h1 className="text-sm text-gray-900 leading-relaxed">
              Enter your access code
            </h1>
          </div>

          <p className="text-xs text-gray-500 mb-6 leading-relaxed">
            This is an invite-only app. Paste your code below to get in.
          </p>

          <div className="my-4">
            <label className="block text-xs font-medium text-gray-500 capitalize tracking-widest mb-2">
              Access code
            </label>

            <div className="relative">
              <input
                type="text"
                placeholder="e.g. ABC123"
                value={code}
                autoFocus
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                className={`w-full pl-4 pr-10 py-3 rounded-xl bg-gray-50 border text-gray-900 text-sm font-mono tracking-widest placeholder:text-gray-400 placeholder:tracking-normal placeholder:font-sans focus:outline-none focus:ring-2 transition-all ${
                  error
                    ? 'border-red-500/60 focus:ring-red-500/30'
                    : 'border-gray-300 focus:border-[#6E3EF5]/50 focus:ring-[#6E3EF5]/30'
                }`}
              />

              <PiMagicWand
                className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-colors ${
                  code ? 'text-[#6E3EF5]' : 'text-gray-400'
                }`}
              />
            </div>

            {error && (
              <p className="mt-2 text-xs text-red-500 flex items-center gap-1.5">
                <span className="w-3.5 h-3.5">⚠</span>
                {error}
              </p>
            )}
          </div>

          <button
            onClick={handleSubmit}
            disabled={isLoading || !code.trim()}
            className="w-full rounded-lg bg-[#6E3EF5] py-3 font-medium text-sm text-white hover:bg-[#6E3EF5]/80 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
          >
            {isLoading ? 'Validating...' : 'Get access'}
          </button>

          <p className="mt-5 text-xs text-gray-500 text-center">
            No code?{' '}
            <a
              href="mailto:info@taraops.com"
              className="text-[#6E3EF5] hover:text-[#6E3EF5]/75 transition-colors"
            >
              Request invite
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
